// Simple toast provider — minimal port of leyro's ToastContext.
// Renders a fixed-bottom toast stack and exposes `addToast(msg, level?)`.
// Levels: default (dark bg), success (rauxa), error (rojo).

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

type Level = 'default' | 'success' | 'error';
interface Toast {
  id: number;
  message: string;
  level: Level;
}

interface ToastApi {
  addToast: (message: string, level?: Level) => void;
}

const Ctx = createContext<ToastApi | null>(null);

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, level: Level = 'default') => {
    const id = ++counter;
    setToasts((cur) => [...cur, { id, message, level }]);
    setTimeout(() => {
      setToasts((cur) => cur.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  return (
    <Ctx.Provider value={{ addToast }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[200] flex flex-col gap-2 max-w-sm">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={[
              'px-4 py-3 rounded-xl text-sm shadow-card-hover animate-slide-up',
              t.level === 'success'
                ? 'bg-rauxa text-white'
                : t.level === 'error'
                  ? 'bg-danger text-white'
                  : 'bg-dark text-white',
            ].join(' ')}
          >
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(Ctx);
  if (!api) {
    // Useful fallback when a component renders outside the provider (tests).
    return { addToast: () => undefined };
  }
  return api;
}

// Standalone effect helper so non-React callers can also push a toast
// (currently unused — kept for parity with leyro).
export function useToastEffect(message: string | null, level: Level = 'default'): void {
  const { addToast } = useToast();
  useEffect(() => {
    if (message) addToast(message, level);
  }, [message, level, addToast]);
}
